(ns dre.pages.search.views.dashboard
  (:require
   ["antd" :refer [Popover Spin]]
   ["react-grid-layout" :as react-grid-layout]
   ["react-sizeme" :as react-sizeme]
   [clojure.set :as set]
   [dre.components.breadcrumb :as breadcrumb]
   [dre.components.button :refer [button]]
   [dre.components.icon :refer [fa-icon icon svg]]
   [dre.components.inputs :as inputs]
   [dre.components.platform-switcher.db :as platforms]
   [dre.components.search-results.views :as results]
   [dre.components.search-results.events :as sr-events]
   [dre.components.searchbar.subs :as-alias searchbar]
   [dre.components.tooltip :refer [tooltip-wrapper]]
   [dre.config :as config]
   [dre.i18n.views :refer [tr]]
   [dre.modals.events :as-alias modals]
   [dre.pages.search.events :as search-events]
   [dre.pages.frontpage.topic-search.events :as topic-events]
   [dre.pages.my-searches.signals.events :as signal-events]
   [dre.pages.search.views.layouts :as layouts]
   [dre.shared.interface.search.pills :as pills]
   [dre.shared.interface.search.util :refer [normalized-blunt]]
   [dre.utils.bem :as bem]
   [dre.utils.query :as query]
   [dre.widgets.db :as widgets-db]
   [dre.widgets.events :as-alias widgets]
   [re-frame.core :as rf]
   [reagent.core :as r]))


(def component-name :dashboard)

(rf/reg-sub
 ::rapid-analysis-enabled?
 (fn [db _]
   (let [user (get-in db [:app :user])
         platforms-config (config/->platforms db)]
     (->> (platforms/registry platforms-config user)
          (filter #(= (:id %) :docanalytics))
          first
          :enabled?))))

(defn- open-in-rapid-content
  []
  (let [title (r/atom "Results")]
    (fn []
      (let [base-url @(rf/subscribe [::config/get [:platforms :docanalytics :url]])
            search-qs (str "?query="
                           (:query/query @(rf/subscribe [::searchbar/search-query [:search]])))
            title-qs (str "?title="
                          @title)
            href (if (not-empty @title)
                   (str base-url search-qs title-qs)
                   (str base-url search-qs))]
        [:div
         {:class (bem/be component-name :open-in-rapid-content)}
         [:div
          {:class (bem/be component-name :open-in-rapid-title-label)}
          "Title"]
         [inputs/input-text
          {:value @title
           :on-change #(reset! title (-> % .-target .-value))}]
         [:a
          {:href href}
          [button
           {:type :brand}
           [:div.icon
            {:class (bem/be component-name :open-in-rapid-external-link)}
            [fa-icon :external-link]]]]]))))

(defn- button-open-in-rapid
  []
  [:> Popover
   {:content (r/as-element [open-in-rapid-content])
    :trigger :click
    :placement :bottomRight}
   [:button
    {:class (bem/bm :button [:bare :medium])}
    [:div.icon [svg :common :timer]]
    [tr "Open in DocAnalytics"]]])

(defn button-save
  []
  (let [search-query @(rf/subscribe [::search-events/search-query])
        signals-db @(rf/subscribe [::signal-events/state])
        blunt (normalized-blunt search-query)
        blunt->signal (when (not-empty blunt) (get-in signals-db signal-events/blunt->signal-path))
        signal (get blunt->signal blunt)
        signal (if signal
                 signal
                 {:query/search-query search-query})
        uuid (:signal/uuid signal)
        new? (not uuid)]
    [tooltip-wrapper "Save your search to your account and set up email notifications."
     [:button
      {:class (bem/bm :button [:light :medium])
       :onClick #(rf/dispatch [::modals/set-modal :my-searches.signals/edit {:data-edit signal}])}
      [icon :common :save]
      (if new?
        [tr "Save Search"]
        [tr "Edit Search"])]]))


(defn button-export
  []
  (let [sr-state @(rf/subscribe [::sr-events/state])
        order (:order sr-state)
        selected-articles @(rf/subscribe [::sr-events/articles-by-guids (:selected sr-state)])
        filtered-query @(rf/subscribe [::search-events/filtered-query])
        boost-query @(rf/subscribe [::sr-events/boost-query])
        ;;boosting logic
        boosting-pills (-> boost-query :search-query :query/pills)
        boosting-pill (query/some-pills-query {:query/pills boosting-pills})
        search-query (if boosting-pill
                       (update filtered-query :query/pills into
                               (concat [pills/boost-op {:pill/type :syntax, :pill/query "l"}] (:pill/pills boosting-pill) [{:pill/type :syntax, :pill/query "r"}]))
                       filtered-query)]
    [tooltip-wrapper "Export the results list, including citations and article information."
     [:button
      {:class (bem/bm :button [:light :medium])
       :onClick #(rf/dispatch [::modals/set-modal :search.export/export {:single-article? false
                                                                         :limit (:total sr-state)
                                                                         :selected-articles selected-articles
                                                                         :search-query search-query
                                                                         :query-order order}])}
      [icon :common :export]
      [tr "Export Results"]]]))


(defn button-share
  []
  (let [search-query @(rf/subscribe [::search-events/search-query])]
    [tooltip-wrapper "Share your search with a colleague. Shared searches appear under the notifications bell and the Shared tab."
     [:button
      {:class (bem/bm :button [:light :medium])
       :onClick #(rf/dispatch [::modals/set-modal :search.share/share {:search-query search-query}])}
      [icon :common :share]
      [tr "Share"]]]))


(defn title
  [{:keys [:query/viewtype :query/question]}]
  [:div
   {:class (bem/be component-name :title)}
   [:div {:class (bem/be component-name :breadcrumb)}
    [breadcrumb/breadcrumb @(rf/subscribe [::breadcrumb/items-results
                                           viewtype
                                           (when (contains? topic-events/topics-map viewtype)
                                             (topic-events/topics-map viewtype))
                                           question])]]
   [:div.actions 
    [button-save]
    [button-share]
    [button-export]
    (when @(rf/subscribe [::rapid-analysis-enabled?])
      [button-open-in-rapid])]])

(defn overview
  [{:keys [all-personalized-widgets-layouts width widgets padding layouts layouts-type]}]
  (let []
    [:> (.-Responsive react-grid-layout)
     {:className "layout"
      :margin [20 20]
      :containerPadding padding
      :isResizable true
      :draggableHandle ".widget-rgl-header"
      :breakpoints (:breakpoints layouts)
      :cols (:cols layouts)
      :layouts (:layouts layouts)
      :measureBeforeMount true
      :useCSSTransforms true
      :allowOverlap false
      :rowHeight 300
      :width width
      :compactType "vertical"
      :onLayoutChange (fn [curr all]
                        (rf/dispatch
                         [::widgets/sync-personalized-widgets-layouts layouts-type all all-personalized-widgets-layouts]))}
     (doall
      (for [w widgets]
        w))]))
  
(defn view
  []
  (let [active-widgets @(rf/subscribe [::widgets/active])
        last-selected-widget @(rf/subscribe [::widgets/last-selected-widget])
        personalized-widgets-layouts  @(rf/subscribe [::widgets/layouts])
        no-widgets? (empty? active-widgets)
        view-type @(rf/subscribe [::widgets/view-type])
        layouts-type (cond
                       no-widgets?
                       {:type "no-widgets"
                        :layouts layouts/layouts-no-widgets}
                       :else
                       {:type "with-widgets"
                        :layouts layouts/layouts-widgets})

        default-layouts (get (:layouts layouts-type) :layouts)
        default-cols (get (:layouts layouts-type) :cols)
        stored-layouts (get
                        (if-let [stored (get personalized-widgets-layouts (:type layouts-type))]
                          {:layouts stored}
                          {})
                        :layouts)
        merged-layouts (into {} (map (fn [k]
                                       [k
                                        (map (fn [value]
                                               (let [new-value (or (first (filter #(= (:i value) (get % "i")) (get (js->clj stored-layouts) k))) {})
                                                     new-value (update-keys new-value keyword)
                                                     v (merge-with (fn [a b] (if (some? b) b a)) value new-value)
                                                     v (if (< (get v :w) (get v :minW))
                                                         (assoc v :w (get v :minW))
                                                         (assoc v :w (get v :w)))]
                                                 v))
                                             (get default-layouts k))])
                                     (keys default-layouts)))
        merged-layouts (into {} (map (fn [k]
                                       [k
                                        (map (fn [value]
                                               (let [v value]
                                                 (cond
                                                   (and last-selected-widget (= (:i v) (name last-selected-widget)))
                                                   (let [space-fn (fn [x y w h]
                                                                    (flatten (map (fn [ys]
                                                                                    (map #(str ys "-" %) (range x (+ x w))))
                                                                                  (range y (+ y h)))))
                                                         existing-widgets (remove #(or
                                                                                    (= (:i %) (name last-selected-widget))
                                                                                    (not (contains? (set (concat ["items"] (map name active-widgets))) (:i %))))
                                                                                  (get merged-layouts k))
                                                         max-y (min 1 (apply max (map #(:y %) existing-widgets)))
                                                         existing-spaces (set (flatten (map #(space-fn
                                                                                              (:x %)
                                                                                              (:y %)
                                                                                              (:w %)
                                                                                              (:h %))
                                                                                            existing-widgets)))
                                                         new-xy (first (remove nil? (apply concat  (map (fn [yy]
                                                                                                          (map (fn [xx]
                                                                                                                 (if (seq (set/intersection (set (space-fn xx yy (:w v) (:h v))) existing-spaces))
                                                                                                                   nil
                                                                                                                   [xx yy]))
                                                                                                               (range (get default-cols k 1))))
                                                                                                        (range (max (inc max-y) 1))))))]
                                                     (if new-xy
                                                       (assoc v :x (first new-xy) :y (second new-xy))
                                                       (assoc v :y 0 :x 0 :replaced? true)))
                                                   :else v)))
                                             (get merged-layouts k))])
                                     (keys merged-layouts)))
        merged-layouts (into {} (map (fn [k]
                                       [k
                                        (map (fn [value]
                                               (let [v value]
                                                 (cond
                                                   (= (get v :i) "items")
                                                   v
                                                   (and last-selected-widget (= (:i v) (name last-selected-widget)))
                                                   v
                                                   last-selected-widget
                                                   (let [new-widget (first (filter #(= (:i %) (name last-selected-widget)) (get merged-layouts k)))
                                                         replaced? (:replaced? new-widget)]
                                                     (if (and replaced?
                                                              (>= (:y v) (:y new-widget))
                                                              (set/intersection (set (range (:x new-widget) (+ (:x new-widget) (:w new-widget))))
                                                                                (set (range (:x v) (+ (:x v) (:w v))))))
                                                       (update v :y inc)
                                                       v))
                                                   :else v)))
                                             (get merged-layouts k))])
                                     (keys merged-layouts)))]
    [:div
     {:class component-name}
     (if @(rf/subscribe [::search-events/loading-blueprints?]) 
       [:> Spin]
       [:<>
        [title @(rf/subscribe [::search-events/search-query])]
        [:div
         {:id "widgets"
          :class (bem/be component-name :widgets)}
         [(r/adapt-react-class react-sizeme/SizeMe)
          {:monitorWidth true}
          (fn [props]
            (let [props (js->clj props :keywordize-keys true)]
              (r/as-element [overview
                             {:all-personalized-widgets-layouts personalized-widgets-layouts
                              :width (:width (:size props))
                              :widgets (concat [[:div
                                                 {:key "items"
                                                  :class :widget-container}
                                                 [results/view]]]
                                               (list (for [{:keys [view id title]} (widgets-db/registry)
                                                           :when (active-widgets id)]
                                                       [:div
                                                        {:key (name id)
                                                         :class :widget-container}
                                                        [view id title]])))
                              :padding [10 10]
                              :layouts-type (:type layouts-type)
                              :layouts (assoc
                                        (:layouts layouts-type) :layouts merged-layouts)}])))]]])]))
